import { useId, useState } from 'react';
import type { DocumentInputField, DocumentInputs } from '@witness/shared/document-inputs';

export function DocumentInputFields({ fields, values, onChange, disabled }: {
  fields: readonly DocumentInputField[];
  values: DocumentInputs;
  onChange: (values: DocumentInputs) => void;
  disabled: boolean;
}) {
  const prefix = useId();
  return <div className="space-y-3">
    {fields.map(field => <div key={field.key} className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <label htmlFor={`${prefix}-${field.key}`} className="text-sm font-medium">
          {field.label}{!field.required && <span className="font-normal text-neutral-500"> (optional)</span>}
        </label>
        <PrintedInputWarning label={field.label} />
      </div>
      <input type="text" id={`${prefix}-${field.key}`} maxLength={field.maxLength}
        required={field.required} disabled={disabled} value={values[field.key] ?? ''}
        aria-describedby={`${prefix}-${field.key}-limit`}
        onChange={event => onChange({ ...values, [field.key]: event.target.value })}
        className="rounded border border-neutral-300 px-3 py-2 text-sm" />
      <p id={`${prefix}-${field.key}-limit`} className="text-xs text-neutral-500">
        A short sentence. Up to {field.maxLength} characters.
      </p>
    </div>)}
  </div>;
}

function PrintedInputWarning({ label }: { label: string }) {
  const id = useId();
  const [hovered, hover] = useState(false);
  const [focused, focus] = useState(false);
  const [pinned, pin] = useState(false);
  const open = hovered || focused || pinned;
  return <span className="relative inline-flex" onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}>
    <button type="button" aria-label={`Printing warning for ${label}`} aria-describedby={open ? id : undefined}
      aria-expanded={open} onFocus={() => focus(true)} onBlur={() => { focus(false); pin(false); }}
      onClick={() => pin(value => !value)}
      onKeyDown={event => { if (event.key === 'Escape') { hover(false); focus(false); pin(false); } }}
      className="rounded-full border border-amber-600 px-1.5 text-xs font-bold text-amber-800 focus:outline focus:outline-2">
      !
    </button>
    {open && <span id={id} role="tooltip" className="absolute left-0 top-full z-10 mt-1 w-64 rounded border border-neutral-300 bg-white p-3 text-sm text-neutral-800 shadow">
      This text will be printed on the ceremony document. Avoid sensitive information.
    </span>}
  </span>;
}
